import React, {useCallback, useEffect, useRef, useState} from 'react';

import {
  Box,
  Flex,
  FormControl,
  HStack,
  IconButton,
  Input,
  Text,
  Tooltip,
} from '@chakra-ui/react';

import {ChevronRightIcon} from '@chakra-ui/icons';
import {motion} from 'framer-motion';
import {FaNetworkWired} from 'react-icons/fa';
import {LuStretchHorizontal} from 'react-icons/lu';
import {useVirtual} from 'react-virtual';

import {useScrollResetOnChange} from '../../hooks/useScrollResetOnChange';
import {HierarchyTrail} from '../HierarchyTrail';
import {hasChildren} from './getHierarchyLevel';
import {PickerItem, useUserPicker} from './useUserPicker';

export interface UserPickerProps<T extends PickerItem> {
  items: T[];
  /** Id del usuario seleccionado (resaltado). */
  selectedId?: string;
  /** Emite el usuario elegido + el trail (ids de ancestros). */
  onSelect: (item: T, parents: string[]) => void;
  /** Restaura el trail de una selección previa. */
  initialParents?: string[] | null;
  /** Reinicia el drill cuando cambia (p. ej. proyecto). */
  resetKey?: unknown;
  /** Slot opcional por fila (badge/ícono/número). El picker no asume contenido. */
  renderRowAccessory?: (item: T) => React.ReactNode;
  /** Email a excluir de niveles hijos; default = JWT. */
  currentUserEmail?: string;
  /** Resolución de nivel inyectable; default = managers+virtualManager. */
  getLevel?: (items: T[], parentId: string | null) => T[];
}

const ROW_BG = '#235db0';
const ROW_BG_SELECTED = '#8ac1bb';

export function UserPicker<T extends PickerItem>(props: UserPickerProps<T>) {
  const {
    items,
    selectedId,
    onSelect,
    initialParents,
    resetKey,
    renderRowAccessory,
    currentUserEmail,
    getLevel,
  } = props;

  const {
    searchString,
    setSearchString,
    isHierarchyView,
    toggleView,
    isSearching,
    trailItems,
    currentParentId,
    drillDown,
    drillUp,
    visibleItems,
    excludeEmail,
  } = useUserPicker<T>({
    items,
    resetKey,
    initialParents,
    currentUserEmail,
    getLevel,
  });

  // Dirección de la transición entre niveles.
  const [direction, setDirection] = useState(0);

  const handleDrillDown = useCallback(
    (id: string) => {
      setDirection(1);
      drillDown(id);
    },
    [drillDown],
  );

  const handleDrillUp = useCallback(
    (id: string) => {
      setDirection(-1);
      drillUp(id);
    },
    [drillUp],
  );

  // El scroll vuelve arriba al cambiar de nivel/búsqueda/vista.
  const scrollKey = `${
    currentParentId ?? ''
  }|${searchString}|${isHierarchyView}`;
  const scrollRef = useScrollResetOnChange<HTMLDivElement>(scrollKey);

  const virtualizer = useVirtual({
    size: visibleItems.length,
    parentRef: scrollRef,
  });

  // Foco al contenedor tras un drill.
  const prevParentIdRef = useRef(currentParentId);
  useEffect(() => {
    const prev = prevParentIdRef.current;
    prevParentIdRef.current = currentParentId;
    if (isHierarchyView && !isSearching && prev !== currentParentId) {
      const timer = setTimeout(() => scrollRef.current?.focus(), 0);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [currentParentId, isHierarchyView, isSearching, scrollRef]);

  const showTrail = isHierarchyView && !isSearching && trailItems.length > 0;
  const levelKey = isSearching ? 'search' : currentParentId ?? 'root';

  const renderRow = (item: T) => {
    if (!item) return null;
    const isSelected = item.id === selectedId;
    const drillable =
      isHierarchyView &&
      !isSearching &&
      hasChildren(items, item.id, {excludeEmail});

    const select = () =>
      onSelect(
        item,
        trailItems.map(trail => trail.id),
      );
    const onSelectKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        select();
      }
    };

    return (
      <Flex
        borderRadius={15}
        transition='all 0.2s ease-in-out'
        py={2}
        px={2}
        align='center'
        w='100%'
        bg={isSelected ? ROW_BG_SELECTED : ROW_BG}
        color='white'
        justify='space-between'
        _hover={{bg: ROW_BG_SELECTED}}>
        {/* Sin `aria-label` a propósito: con uno explícito el lector de
            pantalla dejaría de anunciar el contenido, incluido el accesorio. */}
        <Flex
          flex='1'
          minW={0}
          align='center'
          cursor='pointer'
          role='button'
          tabIndex={0}
          aria-pressed={isSelected}
          onClick={select}
          onKeyDown={onSelectKeyDown}
          sx={{
            '&:focus-visible': {
              outline: 'none',
              boxShadow: 'outline',
              borderRadius: 'sm',
            },
          }}>
          {renderRowAccessory && (
            <Box flexShrink={0} mr={2}>
              {renderRowAccessory(item)}
            </Box>
          )}
          <Flex
            flex='1'
            minW={0}
            direction='column'
            align='flex-start'
            textAlign='left'>
            <Text fontSize={14} noOfLines={2} wordBreak='break-word'>
              {item.name || ''}
            </Text>
            {item.rol && <Text fontSize={10}>{item.rol}</Text>}
          </Flex>
        </Flex>
        {drillable && (
          <Tooltip label={`Explorar equipo de ${item.name}`} placement='left'>
            <IconButton
              size='xs'
              isRound
              flexShrink={0}
              variant='solid'
              bg='white'
              color='gray.700'
              _hover={{bg: 'gray.50'}}
              aria-label={`Explorar equipo de ${item.name}`}
              icon={<ChevronRightIcon w={4} h={4} />}
              onClick={() => handleDrillDown(item.id)}
            />
          </Tooltip>
        )}
      </Flex>
    );
  };

  return (
    <Box w='100%' px={4} py={3} position='relative'>
      <HStack spacing={2} align='center'>
        <FormControl flex='1'>
          <Input
            size='sm'
            type='text'
            placeholder='Buscar usuario'
            bg='white'
            borderRadius={10}
            value={searchString}
            onChange={event => setSearchString(event.target.value)}
          />
        </FormControl>
        <Tooltip
          label={
            isHierarchyView ? 'Visualizar en Lista' : 'Visualizar en Jerarquía'
          }
          placement='left'>
          <IconButton
            size='sm'
            flexShrink={0}
            variant='outline'
            colorScheme='grayBlue'
            aria-label={
              isHierarchyView
                ? 'Visualizar en Lista'
                : 'Visualizar en Jerarquía'
            }
            icon={
              isHierarchyView ? <LuStretchHorizontal /> : <FaNetworkWired />
            }
            onClick={toggleView}
          />
        </Tooltip>
      </HStack>

      {showTrail && (
        <Box mt={2} px={1} overflowX='auto' width='100%' minH={8}>
          <HierarchyTrail items={trailItems} onItemClick={handleDrillUp} />
        </Box>
      )}

      <Box
        ref={scrollRef}
        tabIndex={-1}
        mt={3}
        borderRadius={20}
        bg='white'
        p={3}
        sx={{
          maxHeight: '300px',
          minHeight: '200px',
          overflow: 'auto',
          outline: 'none',
          '&:focus-visible': {boxShadow: 'outline'},
        }}>
        {visibleItems.length === 0 ? (
          <Box py={8} textAlign='center' color='gray.500' fontSize='sm'>
            {isSearching
              ? `No hay usuarios que coincidan con "${searchString.trim()}".`
              : 'No hay usuarios disponibles.'}
          </Box>
        ) : (
          <motion.div
            key={levelKey}
            custom={direction}
            initial={{opacity: 0, x: direction >= 0 ? 32 : -32}}
            animate={{opacity: 1, x: 0}}
            transition={{duration: 0.18, ease: 'easeOut'}}>
            <Box
              style={{
                height: `${virtualizer.totalSize}px`,
                width: '100%',
                position: 'relative',
              }}>
              {virtualizer.virtualItems.map(virtualItem => (
                <Box
                  key={virtualItem.index}
                  ref={virtualItem.measureRef}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    paddingBottom: '10px',
                    transform: `translateY(${virtualItem.start}px)`,
                  }}>
                  {renderRow(visibleItems[virtualItem.index])}
                </Box>
              ))}
            </Box>
          </motion.div>
        )}
      </Box>
    </Box>
  );
}
